import { useRollbar } from '@rollbar/react';
import useCookieHook from 'hooks/useCookieHook';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTE_PREFIX, APP_ROUTE_PREFIX, SUPER_ADMIN_ROUTE_PREFIX, TRAINER_ROUTE_PREFIX } from 'routes/RoutePrefix';
import useRoleAccessRepo from 'services/repo/useRoleAccessRepo';
import { roleAccessListAtom, userMainRoleAtom } from 'store/accessPermission/accessPermissionAtom';
import Constant from 'utils/Constants';
import LocalStorageUtils from 'utils/LocalStorage';
import LunaAlert from 'utils/LunaAlert';
import useCurrentProfileFromTokenHooks from './useCurrentProfileFromTokenHooks';
import useLunaNavigate from './useLunaNavigate';

export const AuthGuardRole = Constant.roleSetting

export default function useAccessPermissionHook(doAuthGuard = false, allowedRole = []) {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const rollbar = useRollbar();

    const [roleAccessList, setRoleAccessList] = useAtom(roleAccessListAtom)
    const [userMainRole, setUserMainRole] = useAtom(userMainRoleAtom)
    const { getRoleAccessByRoleProfile } = useRoleAccessRepo(false)
    const { refreshPrivilege, currentUser } = useCurrentProfileFromTokenHooks()
    const [isLoading, setIsLoading] = useState(true)
    const { redirectToAuth } = useLunaNavigate()
    const { initalAuthCookie } = useCookieHook()

    useEffect(() => {
        if (!doAuthGuard) return;
        checkGuardRole()
    }, [])

    const setUserAccessPermission = async (roleId, profileId, roleKeySetting) => {

        setUserMainRole(roleKeySetting)

        if (AuthGuardRole.SUPERADMIN === roleKeySetting) return true;

        toast.loading(t('accessPermission.getRolePermissionList'))
        const res = await getRoleAccessByRoleProfile(roleId, profileId)

        if (res?.isSuccess) {
            if (res?.data?.length <= 0) {
                toast.dismiss()
                toast.error(t('accessPermission.emptyAccessPermissionList'))
                return false
            }

            setRoleAccessList(res.data)
            return true;
        } else {
            toast.dismiss()
            toast.error(t('accessPermission.failedGetAccessPermissionList'))
            return true;
        }
    }

    const clearPermissionList = () => {
        setUserMainRole(null)
        setRoleAccessList([])
    }

    // this function should only call from choose profile page
    const initiateTokenAndPermissionCall = async () => {
        try {
            toast.loading(t('chooseProfilePage.gettingUserData'))
            const { currentRole, currentRoleSetting, currentProfile } = await refreshPrivilege()
            initalAuthCookie()

            toast.dismiss()

            const res = await setUserAccessPermission(currentRole?.id, currentProfile?.id, currentRoleSetting?.keySetting)
            if (res) {
                toast.dismiss()
                return { status: true }
            }

            return { status: false }

        } catch (error) {
            toast.dismiss()

            rollbar.error(`Error when sending api /mst/user-login/check-token`, {
                'user_token': LocalStorageUtils.jwtToken.get(),
                'error_response': error
            })

            await LunaAlert.error({
                title: error?.message || t('globalAlert.notAuthenticated'),
                description: t('globalAlert.failedGetToken'),
                confirmButtonText: t('common.tryAgain'),
                onConfirmButtonClicked: () => { }
            })

            return { status: false }
        }
    }

    const checkGuardRole = async () => {

        try {

            LocalStorageUtils.loginCheckToken.clear()

            if (LocalStorageUtils.jwtToken.get() === '') {
                setIsLoading(false)
                window.location.replace('/')
                return false;
            }

            if (typeof currentUser?.lstUserDto?.id !== 'undefined' && userMainRole !== null) {
                setIsLoading(false)
                return false;
            }

            setIsLoading(true)
            const { currentRole, currentRoleSetting, currentProfile } = await refreshPrivilege()
            initalAuthCookie()

            if (roleAccessList.length === 0 && userMainRole === null) {
                const res = await setUserAccessPermission(currentRole?.id, currentProfile?.id, currentRoleSetting?.keySetting)
                if (res) {
                    toast.dismiss()
                }
            }

            setIsLoading(false)
            checkPagePermission(currentRoleSetting?.keySetting)
        } catch (error) {

            rollbar.error(`Error when sending api /mst/user-login/check-token`, {
                'user_token': LocalStorageUtils.jwtToken.get(),
                'error_response': error
            })

            redirectToAuth()
        }

    }

    const checkPagePermission = (roleKeySetting) => {

        if (allowedRole.length > 0 && !allowedRole.includes(roleKeySetting.toLowerCase())) {
            if (roleKeySetting?.toLowerCase() === AuthGuardRole.ADMIN.toLowerCase()) return navigate(ADMIN_ROUTE_PREFIX, { replace: true })
            if (roleKeySetting?.toLowerCase() === AuthGuardRole.TRAINEE.toLowerCase()) return navigate(APP_ROUTE_PREFIX, { replace: true })
            if (roleKeySetting?.toLowerCase() === AuthGuardRole.TRAINER.toLowerCase()) return navigate(TRAINER_ROUTE_PREFIX, { replace: true })
            if (roleKeySetting?.toLowerCase() === AuthGuardRole.SUPERADMIN.toLowerCase()) return navigate(SUPER_ADMIN_ROUTE_PREFIX, { replace: true })
        }
    }

    const isAllowView = (menuCode) => {
        if (userMainRole === AuthGuardRole.SUPERADMIN) return true;
        if (!menuCode) return false;
        const menu = roleAccessList.find(m => m.menuCode === menuCode);
        return (menu?.canRead ?? 0) === 1;
    }

    const isAllowAdd = (menuCode) => {
        if (userMainRole === AuthGuardRole.SUPERADMIN) return true;
        if (!menuCode) return false;
        const menu = roleAccessList.find(m => m.menuCode === menuCode);
        return (menu?.canCreate ?? 0) === 1;
    }

    const isAllowEdit = (menuCode) => {
        if (userMainRole === AuthGuardRole.SUPERADMIN) return true;
        if (!menuCode) return false;
        const menu = roleAccessList.find(m => m.menuCode === menuCode);
        return (menu?.canUpdate ?? 0) === 1;
    }

    const isAllowDelete = (menuCode) => {
        if (userMainRole === AuthGuardRole.SUPERADMIN) return true;
        if (!menuCode) return false;
        const menu = roleAccessList.find(m => m.menuCode === menuCode);
        return (menu?.canDelete ?? 0) === 1;
    }

    return {
        roleAccessList,
        clearPermissionList,
        setUserAccessPermission,
        userMainRole,
        setUserMainRole,
        isLoading,
        initiateTokenAndPermissionCall,
        checkPagePermission,

        isAllowView,
        isAllowAdd,
        isAllowEdit,
        isAllowDelete,
    };

}
